import { useState, useEffect, useCallback } from 'react';
import { UserProfile, ChartSetList } from '../types';
import { FeatureKey } from '../utils/features';
import * as drive from '../utils/drive';
import * as db from '../utils/db';
import { getCloudSyncPreference, setCloudSyncPreference } from '../utils/firebase';
import type { User } from 'firebase/auth';

export interface UseDriveSyncReturn {
  userProfile: UserProfile | null;
  isGapiReady: boolean;
  syncStatus: 'idle' | 'syncing' | 'synced' | 'error';
  setSyncStatus: React.Dispatch<React.SetStateAction<'idle' | 'syncing' | 'synced' | 'error'>>;
  showDriveReconnectPrompt: boolean;
  setShowDriveReconnectPrompt: React.Dispatch<React.SetStateAction<boolean>>;
  requestDrivePermissions: () => Promise<void>;
  disconnectDrive: () => Promise<void>;
  fullSync: () => Promise<void>;
}

export function useDriveSync(
  user: User | null,
  hasFeature: (feature: FeatureKey) => boolean,
  setSetLists: React.Dispatch<React.SetStateAction<ChartSetList[]>>,
  loadSavedChartTitles: () => Promise<void>,
): UseDriveSyncReturn {
  // Cloud Sync State
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isGapiReady, setIsGapiReady] = useState(false);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'synced' | 'error'>('idle');
  const [_cloudSyncPreferenceEnabled, setCloudSyncPreferenceEnabled] = useState(false);
  const [showDriveReconnectPrompt, setShowDriveReconnectPrompt] = useState(false);

  // GAPI initialization
  useEffect(() => {
    const initializeDriveAuth = async () => {
      drive.initGoogleClient(() => {
        setIsGapiReady(true);
      });
    };

    initializeDriveAuth();
  }, []);

  // Auto-reconnect to Drive for Premium users
  useEffect(() => {
    const attemptAutoReconnect = async () => {
      if (!user || !isGapiReady || userProfile || !hasFeature('cloudSync')) {
        setShowDriveReconnectPrompt(false);
        return;
      }

      let hadCloudSyncEnabled = false;
      try {
        hadCloudSyncEnabled = await getCloudSyncPreference(user.uid);
        setCloudSyncPreferenceEnabled(hadCloudSyncEnabled);
        console.log('☁️ Cloud Sync preference from Firestore:', hadCloudSyncEnabled);
      } catch (error) {
        console.error('❌ Failed to get Cloud Sync preference:', error);
      }

      if (drive.canAutoReconnect()) {
        console.log('🔄 Attempting auto-reconnect to Drive for Premium user...');

        try {
          const profile = await drive.tryAutoReconnect();
          if (profile) {
            setUserProfile(profile);
            setShowDriveReconnectPrompt(false);
            console.log('✅ Auto-reconnected to Drive:', profile.email);
            return;
          }
        } catch (error) {
          console.error('❌ Auto-reconnect error:', error);
        }
      }

      if (hadCloudSyncEnabled) {
        console.log('🔔 User had Cloud Sync enabled, showing reconnect prompt');
        setShowDriveReconnectPrompt(true);
      } else {
        setShowDriveReconnectPrompt(false);
      }
    };

    attemptAutoReconnect();
  }, [user, isGapiReady, userProfile, hasFeature]);

  // Trigger Drive permissions after Firebase sign-in (if no stored token)
  useEffect(() => {
    if (user && isGapiReady && !userProfile && hasFeature('cloudSync')) {
      console.log('🔑 Firebase user signed in, Drive permissions needed');
    }
  }, [user, isGapiReady, userProfile, hasFeature]);

  // Clear Drive data when user signs out
  useEffect(() => {
    if (!user && userProfile) {
      console.log('👋 User signed out, clearing Drive data');
      drive.signOut();
      setUserProfile(null);
    }
  }, [user, userProfile]);

  // Manual Drive permission function
  const requestDrivePermissions = async () => {
    try {
      console.log('🔑 Manually requesting Drive permissions...');
      const profile = await drive.signIn();
      if (profile) {
        setUserProfile(profile);
        setShowDriveReconnectPrompt(false);
        console.log('✅ Drive permissions granted:', profile.email);

        if (user) {
          try {
            await setCloudSyncPreference(user.uid, true);
            setCloudSyncPreferenceEnabled(true);
            console.log('☁️ Cloud Sync preference saved to Firestore');
          } catch (error) {
            console.error('❌ Failed to save Cloud Sync preference:', error);
          }
        }
      }
    } catch (error) {
      console.error('❌ Drive permission failed:', error);
    }
  };

  // Disconnect from Drive
  const disconnectDrive = async () => {
    setUserProfile(null);
    setShowDriveReconnectPrompt(false);
    console.log('🔌 Drive disconnected from UI');
  };

  const fullSync = useCallback(async () => {
    if (!isGapiReady || !userProfile || syncStatus === 'syncing') return;

    if (!hasFeature('cloudSync')) {
      console.log('Cloud sync requires premium subscription');
      return;
    }

    setSyncStatus('syncing');
    try {
        // Flush offline-delete tombstones first
        const pendingDeletes = await db.getPendingDeletes();
        for (const title of pendingDeletes) {
          await drive.deleteChartFromDrive(title);
          await db.removePendingDelete(title);
          console.log(`Sync: Flushed offline delete for "${title}"`);
        }

        // Sync Charts
        const driveCharts = await drive.listCharts();
        const localCharts = await db.getAllCharts();

        const uploadPromises: Promise<void>[] = [];
        // 1. Upload local charts that are newer or don't exist on Drive
        for (const localChart of localCharts) {
            const driveChart = driveCharts.find(c => c.title === localChart.songTitle);
            if (!driveChart || (localChart.lastModified && driveChart.modifiedTime && new Date(localChart.lastModified) > new Date(driveChart.modifiedTime))) {
                console.log(`Sync: Uploading newer chart "${localChart.songTitle}" to Drive.`);
                uploadPromises.push(drive.saveChartToDrive(localChart));
            }
        }

        const downloadPromises: Promise<void>[] = [];
        // 2. Download remote charts that are newer or don't exist locally
        for (const driveChart of driveCharts) {
            const localChart = localCharts.find(c => c.songTitle === driveChart.title);
            if (!localChart || (driveChart.modifiedTime && localChart.lastModified && new Date(driveChart.modifiedTime) > new Date(localChart.lastModified))) {
                console.log(`Sync: Downloading newer chart "${driveChart.title}" from Drive.`);
                const downloadPromise = drive.getChartContent(driveChart.id).then(chartContent => {
                    if (chartContent) {
                        return db.saveChart(chartContent);
                    }
                });
                downloadPromises.push(downloadPromise as Promise<void>);
            }
        }

        await Promise.all([...uploadPromises, ...downloadPromises]);

        // Sync Set Lists/Collections
        try {
            const localSetLists = await db.getSetLists();
            const cloudSetLists = await drive.loadCollectionsFromDrive();

            if (cloudSetLists.length > 0) {
                console.log(`Sync: Using ${cloudSetLists.length} set lists from cloud`);
                setSetLists(cloudSetLists);
                await db.saveSetLists(cloudSetLists);
            } else if (localSetLists.length > 0) {
                console.log(`Sync: Uploading ${localSetLists.length} local set lists to cloud`);
                await drive.saveCollectionsToDrive(localSetLists);
            }
        } catch (error) {
            console.error("Failed to sync set lists:", error);
        }

        await loadSavedChartTitles();
        setSyncStatus('synced');
    } catch (error) {
        console.error("Google Drive full sync failed:", error);
        setSyncStatus('error');
    }
  }, [isGapiReady, userProfile, loadSavedChartTitles]);

  // Background sync and online/offline handling
  useEffect(() => {
      const handleOnline = () => {
          if (userProfile) {
              console.log('App is back online. Attempting to sync...');
              fullSync();
          }
      };

      window.addEventListener('online', handleOnline);

      const interval = setInterval(() => {
          if (syncStatus === 'error' && navigator.onLine && userProfile) {
              console.log('Retrying failed sync...');
              fullSync();
          }
      }, 60000);

      return () => {
          window.removeEventListener('online', handleOnline);
          clearInterval(interval);
      };
  }, [syncStatus, userProfile, fullSync]);

  return {
    userProfile,
    isGapiReady,
    syncStatus,
    setSyncStatus,
    showDriveReconnectPrompt,
    setShowDriveReconnectPrompt,
    requestDrivePermissions,
    disconnectDrive,
    fullSync,
  };
}
